"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const generate_helpers_1 = require("./value-types/generate-helpers");
const value_wrappers_1 = require("./value-wrappers");
const expressions_1 = require("./parsers/expressions");
const object_properties_1 = require("./lib/object-properties");
const json_compare_1 = require("./lib/json-compare");
// Generator creation helper function.
const makeGenerator = (name, get) => ({ name, get });
const getParentIds = (allValues, childId, soFar = []) => {
    var _a;
    const parentId = (_a = allValues.find((v) => v.id === childId)) === null || _a === void 0 ? void 0 : _a.parentId;
    return parentId ? getParentIds(allValues, parentId, [parentId, ...soFar]) : soFar;
};
// Return a generator that will produce an array of values.
const listGenerator = (generator, listSpec) => (values, level = 0) => {
    var _a, _b;
    const listLength = (0, expressions_1.evaluateExpression)((_a = listSpec.length) !== null && _a !== void 0 ? _a : '', (0, value_wrappers_1.unwrapState)(values !== null && values !== void 0 ? values : {})).value;
    // If we want unique values,
    // try to generate the specified number of unique values by using a set.
    // Limit the number of attempts to produce a set of the specified size.
    // (It's possible that the generator just can't produce that many values).
    if (listSpec.isSet) {
        let count = 0;
        let valueSet = new Set();
        // E.g. if we want 5 unique values, try at most 500 times.
        while (valueSet.size < listLength && count < listLength * 100) {
            // For the set to distinguish between objects, we stringify them.
            const valuesWithIndex = values !== null && values !== void 0 ? values : {};
            valuesWithIndex.listIndex = valueSet.size;
            valueSet.add(JSON.stringify((_b = generator(valuesWithIndex, level).value) !== null && _b !== void 0 ? _b : ''));
            count++;
        }
        // Turn the strings from the set back into objects.
        return (0, value_wrappers_1.makeValue)([...valueSet].map((v) => JSON.parse(v)), generator(Object.assign(Object.assign({}, values), { listIndex: 0 }), 0).variables);
    }
    // If we don't want unique values, just run the generator the required number of times.
    return (0, value_wrappers_1.makeValue)(listLength > 0 ?
        Array(listLength).fill('').map((_, i) => {
            return generator(Object.assign(Object.assign({}, values), { listIndex: i }), level).value;
        }) : [], generator(Object.assign(Object.assign({}, values), { listIndex: 0 }), 0).variables);
};
// Return a function that generates a set of values based on the spec, presets and value-types passed in.
function generator(valuesSpec, generatorPresets = {}, typeSpecs = {}, functionSpecs = {}) {
    function valueGenerator(valueTypeSpec, rules = [{}], exclusions = '') {
        // The valueTypeSpec either has a functionId or an inputs object.
        var _a;
        // For a functionId we can immediately produce a generator.
        if (valueTypeSpec.functionId) {
            // We can find the function spec for the specified function id, ...
            const functionSpec = functionSpecs[valueTypeSpec.functionId];
            // ... and a generate function for the function spec and values spec, ...
            const generate = (0, generate_helpers_1.getGenerateFn)(functionSpec, valuesSpec.values, typeSpecs);
            // ... and return a generator that uses that generate function.
            return (state, level = 0) => {
                let value;
                let retryCount = 1000;
                const evaluatedExclusions = exclusions
                    ? exclusions
                        .split(',')
                        .flatMap((exc) => (0, expressions_1.evaluateExpression)(exc, (0, value_wrappers_1.unwrapState)(state !== null && state !== void 0 ? state : {})).value)
                    : [];
                do {
                    const proposedValue = generate(rules[Math.min(level, rules.length - 1)], state !== null && state !== void 0 ? state : {});
                    if (evaluatedExclusions.every((exc) => !(0, json_compare_1.jsonCompare)(exc, proposedValue.value))) {
                        value = proposedValue;
                    }
                    retryCount--;
                } while (retryCount && !value);
                return value !== undefined ? value : (0, value_wrappers_1.makeError)(`The value could not be generated.`);
            };
        }
        else {
            // For an inputs object, we'll need to produce a generator that generates values
            // for each object property.
            const fnsObj = {};
            const valueTypeFields = Object.entries((_a = valueTypeSpec.inputs) !== null && _a !== void 0 ? _a : valueTypeSpec.fields);
            // We still return a single generator function.
            return (state, level = 0) => {
                // But we need to produce a nested generator for each object property.
                // That nested generator may have nested generators of its own.
                // Leaf properties will have functionIds and will produce a generator that returns a value.
                for (const [key, valueTypeId] of valueTypeFields) {
                    const typeSpec = typeSpecs[valueTypeId];
                    fnsObj[key] = typeSpec
                        ? valueGenerator(typeSpec, [((rules !== null && rules !== void 0 ? rules : [{}])[Math.min(level, rules.length - 1)])[key]])
                        : () => (0, value_wrappers_1.makeError)(`The value type spec could not be found.`);
                }
                // When the single generator function is called, it calls the nested generators
                // for each inputs object property.
                const localState = {};
                const objEntries = Object.entries(fnsObj).map(([key, fn]) => {
                    const localResult = fn(Object.assign(Object.assign({}, state), localState));
                    localState[key] = localResult;
                    return [key, localResult];
                });
                const result = Object.fromEntries(objEntries.map(([key, obj]) => [key, obj.value]));
                const variables = objEntries.flatMap(([key, obj]) => obj.variables);
                return (0, value_wrappers_1.makeValue)(result, variables);
            };
        }
    }
    // Build up an array of generators, one for each value in the spec.
    function getGenerator(valueSpec) {
        var _a, _b;
        const valueTypeSpec = typeSpecs[valueSpec.type];
        if (!valueTypeSpec) {
            return makeGenerator(valueSpec.name, () => (0, value_wrappers_1.makeError)(`The value type spec for the ${valueSpec.name} value could not be found`));
        }
        let rules = valueSpec.rules;
        if (valueTypeSpec.name === 'Generator') {
            const values = (_b = (_a = rules[0].generator) === null || _a === void 0 ? void 0 : _a.values) !== null && _b !== void 0 ? _b : [];
            return makeGenerator(valueSpec.name, (state, level, updatedValues, oldValues) => (0, value_wrappers_1.makeValue)(values
                .map(getGenerator)
                .reduce((soFar, { name, get }) => {
                soFar[name] = get(soFar, level, updatedValues, oldValues).value;
                return soFar;
            }, {})));
        }
        const itemGenerator = valueGenerator(valueTypeSpec, rules, valueSpec.exclusions);
        const generator = valueSpec.list ? listGenerator(itemGenerator, valueSpec.list) : itemGenerator;
        return makeGenerator(valueSpec.name, generator);
    }
    const valuesWithFullNamePaths = valuesSpec.values.map((v) => {
        const pathIds = getParentIds(valuesSpec.values, v.id);
        return Object.assign(Object.assign({}, v), { name: pathIds
                .map((id) => { var _a, _b; return (_b = (_a = valuesSpec.values.find((val) => val.id === id)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : id; })
                .concat(v.name)
                .join('.') });
    });
    const generators = valuesWithFullNamePaths.map(getGenerator);
    // Return a function that will call the generators to produce an object of values.
    return (presets = {}, level = 0, updatedValues = {}, oldValues = {}) => {
        var _a, _b;
        let newValues = generators.reduce((soFar, { name, get }) => {
            var _a, _b, _c, _d, _e, _f;
            if (Object.values(soFar.values).find((v) => v.kind === 'error'))
                return soFar;
            const value = get(soFar.values, level);
            const updatedValuesForName = Object.entries(updatedValues)
                .filter(([key]) => key === name || key.split('.')[0] === name);
            if (updatedValuesForName.length) {
                if (Array.isArray((_a = oldValues.state) === null || _a === void 0 ? void 0 : _a[name])) {
                    const oldArray = (_b = oldValues.state) === null || _b === void 0 ? void 0 : _b[name];
                    const valueObject = updatedValuesForName.length === 1 && !updatedValuesForName[0][0].includes('.')
                        ? updatedValuesForName[0][1]
                        : updatedValuesForName.reduce((arr, [key, val]) => {
                            const [first, ...nested] = key.split('.');
                            arr[key] = (0, object_properties_1.setObjectProperty)(oldArray, nested.length ? nested.join('.') : key, val);
                            return arr;
                        }, oldArray);
                    soFar.values[name] = (0, value_wrappers_1.makeValue)(valueObject, []);
                }
                else {
                    const valueObject = updatedValuesForName.reduce((obj, [key, val]) => {
                        if (key === name)
                            return Object.assign(Object.assign({}, obj), { [key]: val });
                        const [first, ...nested] = key.split('.');
                        return (0, object_properties_1.setObjectProperty)(obj, nested.length ? nested.join('.') : key, val);
                    }, Object.assign({}, oldValues.state[name]));
                    soFar.values[name] = (0, value_wrappers_1.makeValue)((_c = valueObject[name]) !== null && _c !== void 0 ? _c : valueObject, []);
                }
                soFar.updated.push(name);
            }
            else if (Object.assign(Object.assign({}, generatorPresets), presets)[name] !== undefined) {
                soFar.values[name] = (_d = presets[name]) !== null && _d !== void 0 ? _d : generatorPresets[name];
                soFar.updated.push(name);
            }
            else if (((_e = value.variables) !== null && _e !== void 0 ? _e : []).some((v) => soFar.updated.includes(v) || ((v === null || v === void 0 ? void 0 : v.split) && soFar.updated.includes(v.split('.')[0])))) {
                soFar.values[name] = value;
                soFar.updated.push(name);
            }
            else if (Object.keys((_f = oldValues.state) !== null && _f !== void 0 ? _f : {}).length === 0) {
                soFar.values[name] = value;
            }
            else {
                soFar.values[name] = (0, value_wrappers_1.makeValue)((0, object_properties_1.getObjectProperty)(oldValues.state, name, {}), value.variables);
            }
            return {
                values: Object.entries(soFar.values).reduce((objState, [prop, propVal]) => {
                    var _a, _b;
                    const [first, ...nested] = prop.split('.');
                    if (!nested.length)
                        objState[prop] = propVal;
                    if (nested.length) {
                        const obj = (_b = (_a = objState[first]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : {};
                        objState[first] = (0, value_wrappers_1.makeValue)((0, object_properties_1.setObjectProperty)(obj, nested.join('.'), propVal.value), []);
                    }
                    return objState;
                }, {}),
                updated: soFar.updated,
            };
        }, { values: {}, updated: [] }).values;
        const presetEntries = Object.entries(Object.assign(Object.assign(Object.assign(Object.assign({}, oldValues.state), generatorPresets), presets), updatedValues));
        const nonSpecPresets = presetEntries.filter(([name]) => generators.findIndex((g) => g.name === name) === -1);
        for (const [name, value] of nonSpecPresets) {
            const [first, ...nested] = name.split('.');
            if (!nested.length) {
                newValues[name] = (0, value_wrappers_1.makeValue)(value, []);
            }
            else {
                newValues[first] = (0, value_wrappers_1.makeValue)((0, object_properties_1.setObjectProperty)((_b = (_a = newValues[first]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : {}, nested.join('.'), value), []);
            }
        }
        // Construct objects for values that use dot notation
        // newValues = Object.entries(newValues).reduce((soFar, [key, value]) => {
        //   const [first, ...nested] = key.split('.');
        //   if (!nested.length) soFar[key] = value;
        //   if (nested.length) {
        //     const obj = soFar[first]?.value ?? {};
        //     soFar[first] = makeValue(setObjectProperty(obj, nested.join('.'), value.value), []);
        //   }
        //   return soFar;
        // }, {} as { [key: string]: any });
        console.log({ presets, level, updatedValues, oldValues, newValues });
        return newValues;
    };
}
exports.default = generator;
